import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { socialLinks, musicPlatforms } from '../../data/socialLinks';
import { events } from '../../data/events';
import FloatingNotesBackground from './FloatingNotesBackground';
import './Hero.css';

const Hero = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();

  const logoPath = theme === 'dark'
    ? '/eevn_logo_dark.png'
    : '/eevn_logo_light.png';

  const scrollToMusic = () => {
    const element = document.querySelector('#music');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Get upcoming events marked for hero display
  const heroEvents = events.filter(event => {
    if (!event.displayInHero) return false;

    // TBA dates are always considered upcoming
    if (event.date === "TBA" || event.date === null || event.date === "") {
      return true;
    }

    const eventDate = new Date(event.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return eventDate >= today;
  }).sort((a, b) => {
    // TBA dates go to the end
    if (a.date === "TBA" || a.date === null || a.date === "") return 1;
    if (b.date === "TBA" || b.date === null || b.date === "") return -1;
    return new Date(a.date) - new Date(b.date);
  });

  const scrollToEvents = () => {
    if (heroEvents.length > 0) {
      navigate('/events/' + heroEvents[0].slug);
    }
  };

  // Format date for display
  const formatEventDate = (dateStr) => {
    if (!dateStr || dateStr === "TBA") return dateStr;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <section className="hero section" id="hero">
      <FloatingNotesBackground />
      <div className="hero-content">
        <div className="hero-logo">
          <img src={logoPath} alt="eevn" className="hero-logo-img" />
        </div>
        <h1 className="hero-tagline">{t('hero.tagline')}</h1>
        <p className="hero-description">
          {t('about.location')}
        </p>
        {heroEvents.length > 0 && (
          <div className="hero-next-show" onClick={scrollToEvents}>
            <span className="hero-next-show-label">{t('hero.nextShow')}</span>
            <span className="hero-next-show-sep">·</span>
            <span className="hero-next-show-info">
              {formatEventDate(heroEvents[0].date)} · {heroEvents[0].venue}, {heroEvents[0].location}
            </span>
            <span className="hero-next-show-arrow">→</span>
          </div>
        )}
        <div className="hero-actions">
          <button className="btn-primary" onClick={scrollToMusic}>
            {t('hero.listenNow')}
          </button>
        </div>
        <div className="hero-music-platforms">
          <h3 className="hero-platforms-title">Listen on</h3>
          <div className="hero-platforms">
            <a 
              href={musicPlatforms.spotify} 
              target="_blank" 
              rel="noopener noreferrer"
              className="music-platform-link"
              aria-label="Spotify"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.84-.179-.96-.539-.12-.421.18-.84.54-.96 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.302 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/>
              </svg>
              <span>Spotify</span>
            </a>
            <a 
              href={musicPlatforms.appleMusic} 
              target="_blank" 
              rel="noopener noreferrer"
              className="music-platform-link"
              aria-label="Apple Music"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 2c5.514 0 10 4.486 10 10s-4.486 10-10 10S2 17.514 2 12 6.486 2 12 2zm-1 14.5l6-3.5-6-3.5v7z"/>
              </svg>
              <span>Apple Music</span>
            </a>
            <a 
              href={musicPlatforms.youtube} 
              target="_blank" 
              rel="noopener noreferrer"
              className="music-platform-link"
              aria-label="YouTube"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              <span>YouTube</span>
            </a>
            <a 
              href={musicPlatforms.youtubeMusic} 
              target="_blank" 
              rel="noopener noreferrer"
              className="music-platform-link"
              aria-label="YouTube Music"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              <span>YouTube Music</span>
            </a>
            <a 
              href={musicPlatforms.tidal} 
              target="_blank" 
              rel="noopener noreferrer"
              className="music-platform-link"
              aria-label="Tidal"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 2c5.514 0 10 4.486 10 10s-4.486 10-10 10S2 17.514 2 12 6.486 2 12 2z"/>
                <path d="M7 8h2v8H7V8zm4-2h2v10h-2V6zm4 4h2v6h-2v-6z"/>
              </svg>
              <span>Tidal</span>
            </a>
          </div>
        </div>
        <div className="hero-social">
          <a 
            href={socialLinks.instagram} 
            target="_blank" 
            rel="noopener noreferrer"
            className="social-link"
            aria-label="Instagram"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
          </a>
          <a 
            href={socialLinks.youtube} 
            target="_blank" 
            rel="noopener noreferrer"
            className="social-link"
            aria-label="YouTube"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Hero;

